import { router } from '@/plugins/router/index'
import {
	useAuthStore,
	useBackendStore,
	useCancelTokensStore,
	useUserStore,
	useToastStore,
	useAlertStore,
} from '@/stores/system'
import { useLiaStore } from '@/stores/system/lia'
import {
	isEscalatableNetworkFailure,
	recordTransportFailure,
	resetTransportFailures,
} from '@/helpers/axios/transportFailure'
import { Capacitor } from '@capacitor/core'
import Axios, {
	AxiosError,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from 'axios'

// Extend axios request config with our custom meta fields so TypeScript knows
// about `meta.name` (already used below) and the new `meta.silentError` flag.
declare module 'axios' {
	interface AxiosRequestConfig {
		meta?: {
			/** Marks app-level requests that should skip the /api/v1 baseURL rewrite. */
			name?: string
			/**
			 * When true, responseOnRejected will NOT pop the global alert dialog
			 * and will NOT navigate to the error route.  The rejection is still
			 * forwarded so callers (e.g. TanStack Query) can retry normally.
			 * Use for batch / preload request fans where transient failures should
			 * be handled by the retry layer, not surfaced as user-visible errors.
			 */
			silentError?: boolean
		}
	}
}

function sleep(time) {
	return new Promise((resolve) => {
		setTimeout(resolve, time)
	})
}

function loadAuth() {
	const userStore = useUserStore()

	const auth =
		typeof userStore.auth !== 'undefined'
			? userStore.auth
			: useAuthStore().getCookie()
	const expiresAt = auth?.expires_in + auth?.authorized_at - 60

	return {
		auth: auth,
		now: Math.floor(Date.now() / 1000),
		expiresAt,
		rememberMe: expiresAt + auth?.rememberMe,
	}
}

function loadLia(config: InternalAxiosRequestConfig) {
	const { person } = useLiaStore()

	if (
		typeof person !== 'undefined' &&
		person.user_id !== null &&
		!isNaN(person.user_id)
	) {
		config.headers['lia'] = person.user_id
	}

	return config
}

function loadCancelToken(config: InternalAxiosRequestConfig) {
	const CancelToken = Axios.CancelToken
	const source = CancelToken.source()
	config.cancelToken = source.token

	const cancelTokensStore = useCancelTokensStore()
	cancelTokensStore.add({ token: source, url: config.url })

	return config
}

export async function devInterceptor(config: InternalAxiosRequestConfig) {
	const baseURL = import.meta.env.VITE_API_BASEURL
	const urls = [baseURL, baseURL + 'api/v1', baseURL + 'app']

	if (Capacitor.getPlatform() === 'android' && import.meta.env.DEV) {
		config.baseURL = config.baseURL?.replace(baseURL, 'http://10.0.2.2:8888/')
	}

	return config
}

export async function addSubdomainInterceptor(
	config: InternalAxiosRequestConfig
) {
	config.headers['HPAPI-DOM'] = useBackendStore().url?.subdomain
	return config
}

export async function requestInterceptor(config: InternalAxiosRequestConfig) {
	const authStore = useAuthStore()
	const userStore = useUserStore()
	const toastStore = useToastStore()

	config = loadCancelToken(config)

	let { auth, now, expiresAt, rememberMe } = loadAuth()
	if (!auth) {
		return config
	}

	if (authStore.isRefreshing) {
		let i = 0
		while (i++ < 10 && authStore.isRefreshing) {
			await sleep(1000)
		}
		if (authStore.isRefreshing) {
			return await userStore.logout()
		} else {
			;({ auth, now, expiresAt, rememberMe } = loadAuth())
		}
	}

	if ((isNaN(expiresAt) || expiresAt > now) && auth) {
		const baseUrlWithoutApi = import.meta.env.VITE_API_BASEURL
		if (
			!config.url.includes(`api/${import.meta.env.VITE_API_VERSION}`) &&
			config?.meta?.name !== 'app'
		) {
			config.baseURL = `${baseUrlWithoutApi}api/${import.meta.env.VITE_API_VERSION}`
		}
		config.headers['Authorization'] = 'Bearer ' + auth.access_token

		loadLia(config)
		return config
	}

	if (rememberMe <= now) {
		return userStore.logout().then(() => {
			toastStore.toastInfo({
				message: 'component.toast.info.sessionExpired',
			})
		})
	}

	return userStore
		.refresh()
		.then(({ data }) => {
			//FIXME
			config.headers['Authorization'] = 'Bearer ' + data.access_token
			return loadLia(config)
		})
		.catch((error) => {
			throw new Error(error)
		})
}

export function responseOnFulfilled(response: AxiosResponse) {
	const backendStore = useBackendStore()

	resetTransportFailures()

	backendStore.dataSet({ status: 200, statusText: null })
	if (response.config.url) {
		useCancelTokensStore().remove(response.config.url)
	}

	// const { status, statusText, data } = response

	// if (response.data.toString().length === 0) return { status, statusText }
	// return response.data

	return response
}

export function responseOnRejected(result: AxiosError) {
	// AAR-1670: symmetrical to responseOnFulfilled. Without this the store kept a
	// CancelToken for every failed request forever.
	if (result.config?.url) {
		useCancelTokensStore().remove(result.config.url)
	}

	let error = result.response?.data?.error
	if (
		typeof error === 'undefined' &&
		typeof result.response?.data?.errors !== 'undefined'
	) {
		error =
			result.response?.data.errors[Object.keys(result.response?.data.errors)]
		error = Array.isArray(error) && error.length > 0 ? error[0] : undefined
	}
	const error_info = {
		data: result.response?.data,
		endpoint: result.config?.url?.replace(`${result.config.baseURL}`, ''),
		error,
		headers: result.response?.headers,
		message: result.response?.data?.message || result.message,
		result: false,
		status: result.request?.status,
		statusText: result.request?.statusText,
	}
	if (error_info.message === 'Network Error') {
		error_info.status = 500
		error_info.statusText = result.message
	}

	// Opt-in: callers that set meta.silentError = true (e.g. Protocol batch-loader)
	// want the query layer to handle retries quietly.  Skip the global alert dialog
	// and router redirects so a single transient failure never pops a modal or
	// navigates the user away mid-load.  The rejection is still forwarded.
	const isSilent = !!result.config?.meta?.silentError

	// One network failure is not proof the backend is down (see
	// transportFailure.ts): suppress the global escalation and let the caller's own
	// catch deal with it.  A second one inside the window escalates exactly as
	// before, which is what a real outage looks like.
	//
	// `!isSilent` first, and not merely as part of suppressGlobalError below: a
	// request that opted out of global errors must not prime the window either,
	// otherwise a silent batch fan-out (Protocol.vue) or a failed thumbnail would
	// make the NEXT ordinary request escalate on its first failure — re-creating
	// the very modal this is meant to prevent.
	const isTransientNetworkFailure =
		!isSilent &&
		isEscalatableNetworkFailure(result) &&
		recordTransportFailure().isFirstInWindow

	const suppressGlobalError = isSilent || isTransientNetworkFailure

	if (
		(error_info.status === 500 &&
			error_info.message &&
			error_info.message === 'Network Error') ||
		(error_info.status === 403 &&
			error_info.error &&
			error_info.error !== 'unauthorized' &&
			error_info.error !== 'This action is unauthorized.' &&
			// AAR-300: ein deaktiviertes Teammitglied bekommt auf JEDEN Mandanten-Request
			// 403 von der ActiveTeamMember-Middleware — auch auf whoami direkt nach dem
			// Login. Ohne diesen Ausschluss setzt das `backendStore.dataSet` und damit
			// `isAvailable = false`, und der Login-Screen behauptet "Anwendung ist nicht
			// erreichbar". Das ist falsch: der Server antwortet, der Nutzer darf nur nicht.
			error_info.error !== 'team member deactivated' &&
			error_info.error !== 'invalid_subscription') ||
		(error_info.status === 403 &&
			error_info.error === 'invalid_subscription' &&
			error_info.data.code === 3001) ||
		error_info.status === 503 ||
		error_info.status === 502
	) {
		// Silent (opt-in) requests must not drive the global backend-health status
		// either: isAvailable = (status === 200) gates Errors.vue's full-screen error
		// view, so a transient batch failure would otherwise flash that screen even
		// with the alert dialog already suppressed below.
		if (!suppressGlobalError) {
			const backendStore = useBackendStore()
			backendStore.dataSet(error_info)
			const alertStore = useAlertStore()
			alertStore.errorAlert(error_info)
		}
	}
	if (error_info.status === 404 && error_info.error === 'model_not_found') {
		// Skip the global error-route redirect for missing image binaries —
		// a stale image FK (binary deleted, ID still referenced) is a benign
		// tenant-data condition that should NOT navigate the user away from
		// whatever they're doing. The image store catches the rejection at
		// src/stores/modules/image.ts and returns null so callers can render
		// a placeholder.
		if (!/\/images\/\d+\/binary$/.test(error_info.endpoint ?? '')) {
			if (!suppressGlobalError) {
				router.replace({
					name: 'error',
					params: { id: error_info.status, error: error_info.data },
				})
			}
		}
	}
	if (error_info.status === 403) {
		if (
			error_info.data?.error !== 'no database' &&
			!(
				error_info.error === 'invalid_subscription' &&
				[1000, 3001].includes(error_info.data.code)
			)
		) {
			if (!suppressGlobalError) {
				router.push({
					name: 'error',
					params: { id: 403, error: error_info.data },
				})
			}
		}
	}
	if (process.env.NODE_ENV === 'development') {
		console.error(error_info)
		console.dir(result)
	}
	return Promise.reject(error_info)
}
